import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, ChevronRight, ChevronLeft, User, TrendingUp, Building, Phone, FileCheck, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import theme from '../theme';
import ApiService from '../services/api';

// Custom StepConnector for elegant golden gradient lines
const CustomStepConnector = () => (
  <StepConnector
    sx={{
      '& .MuiStepConnector-line': {
        height: 2,
        border: 0,
        backgroundColor: 'rgba(212, 167, 71, 0.15)',
        borderRadius: 1,
        marginTop: '-1px',
        position: 'relative',
        zIndex: 1,
      },
      '&.Mui-active .MuiStepConnector-line': {
        background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
      },
      '&.Mui-completed .MuiStepConnector-line': {
        background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
      },
    }}
  />
);

const Sponsor = () => {
  const { t } = useTranslation('sponsor');
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    { label: t('stepper.contactInfo'), icon: <User size={20} /> },
    { label: t('stepper.sponsorshipInterest'), icon: <TrendingUp size={20} /> },
    { label: t('stepper.organizationDetails'), icon: <Building size={20} /> },
    { label: t('stepper.communicationPreferences'), icon: <Phone size={20} /> },
    { label: t('stepper.reviewSubmit'), icon: <FileCheck size={20} /> },
  ];
  const [formData, setFormData] = useState({
    fullName: '',
    designation: '',
    organization: '',
    email: '',
    mobile: '',
    city: '',
    state: '',
    sponsorshipType: '',
    budgetRange: '',
    modeOfAssociation: '',
    organizationType: '',
    website: '',
    organizationNote: '',
    preferredContactMethod: '',
    bestTimeToReach: '',
    receiveUpdates: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Modern field styling matching CategoryLearnMore
  const modernFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'rgba(212, 167, 71, 0.02)',
      border: '1px solid rgba(212, 167, 71, 0.15)',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(212, 167, 71, 0.04)',
        borderColor: 'rgba(212, 167, 71, 0.3)',
        '& fieldset': { border: 'none' }
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(212, 167, 71, 0.05)',
        borderColor: '#D4A747',
        boxShadow: '0 0 0 3px rgba(212, 167, 71, 0.1)',
        '& fieldset': { border: 'none' }
      },
      '&.Mui-error': {
        backgroundColor: 'rgba(244, 67, 54, 0.02)',
        borderColor: 'rgba(244, 67, 54, 0.3)',
        '&:hover': {
          backgroundColor: 'rgba(244, 67, 54, 0.04)',
          borderColor: 'rgba(244, 67, 54, 0.5)'
        },
        '&.Mui-focused': {
          backgroundColor: 'rgba(244, 67, 54, 0.05)',
          borderColor: '#f44336',
          boxShadow: '0 0 0 3px rgba(244, 67, 54, 0.1)'
        }
      },
      '& fieldset': { border: 'none' }
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      fontSize: '0.875rem',
      fontWeight: 500,
      backgroundColor: 'transparent',
      '&.MuiInputLabel-shrink': {
        backgroundColor: 'white',
        padding: '0 6px',
        marginLeft: '-6px',
        borderRadius: '4px'
      },
      '&.Mui-focused': {
        color: '#D4A747'
      },
      '&.Mui-error': {
        color: '#f44336'
      }
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
      marginLeft: 0,
      marginTop: '6px',
      fontWeight: 400
    }
  };

  const sponsorshipTypes = [
    { value: '1. Title Sponsor / Platinum', label: t('sponsorshipInterest.sponsorshipTypes.platinum') },
    { value: '2. Gold', label: t('sponsorshipInterest.sponsorshipTypes.gold') },
    { value: '3. Silver', label: t('sponsorshipInterest.sponsorshipTypes.silver') },
    { value: '4. Bronze', label: t('sponsorshipInterest.sponsorshipTypes.bronze') },
    { value: 'Agri Associate', label: t('sponsorshipInterest.sponsorshipTypes.agriAssociate') },
    { value: 'General Associate', label: t('sponsorshipInterest.sponsorshipTypes.generalAssociate') },
  ];

  const budgetRanges = [
    { value: '₹ 25K – ₹ 1L', label: t('sponsorshipInterest.budgetRanges.range1') },
    { value: '₹ 1L – ₹ 5L', label: t('sponsorshipInterest.budgetRanges.range2') },
    { value: '₹ 5L – ₹ 10L', label: t('sponsorshipInterest.budgetRanges.range3') },
    { value: '₹ 10L+', label: t('sponsorshipInterest.budgetRanges.range4') },
  ];

  const modesOfAssociation = [
    { value: 'CSR', label: t('sponsorshipInterest.modesOfAssociation.csr') },
    { value: 'Marketing', label: t('sponsorshipInterest.modesOfAssociation.marketing') },
    { value: 'Brand Collaboration', label: t('sponsorshipInterest.modesOfAssociation.brandCollaboration') },
    { value: 'Individual Support', label: t('sponsorshipInterest.modesOfAssociation.individualSupport') },
    { value: 'Other', label: t('sponsorshipInterest.modesOfAssociation.other') },
  ];

  const organizationTypes = [
    { value: 'Corporate', label: t('organizationDetails.organizationTypes.corporate') },
    { value: 'NGO', label: t('organizationDetails.organizationTypes.ngo') },
    { value: 'Foundation', label: t('organizationDetails.organizationTypes.foundation') },
    { value: 'MSME', label: t('organizationDetails.organizationTypes.msme') },
    { value: 'Individual', label: t('organizationDetails.organizationTypes.individual') },
    { value: 'Government Body', label: t('organizationDetails.organizationTypes.governmentBody') },
  ];

  const contactMethods = [
    { value: 'Email', label: t('communicationPreferences.contactMethods.email') },
    { value: 'Phone', label: t('communicationPreferences.contactMethods.phone') },
    { value: 'WhatsApp', label: t('communicationPreferences.contactMethods.whatsapp') },
  ];

  const timings = [
    { value: 'Morning', label: t('communicationPreferences.timings.morning') },
    { value: 'Afternoon', label: t('communicationPreferences.timings.afternoon') },
    { value: 'Evening', label: t('communicationPreferences.timings.evening') },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateStep = (step) => {
    console.log('validateStep', step);
    const newErrors = {};

    if (step === 0) {
      if (!formData.fullName.trim()) newErrors.fullName = t('contactInfo.errors.fullName');
      if (!formData.designation.trim()) newErrors.designation = t('contactInfo.errors.designation');
      if (!formData.organization.trim()) newErrors.organization = t('contactInfo.errors.organization');
      if (!formData.email.trim()) {
        newErrors.email = t('contactInfo.errors.email');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('contactInfo.errors.emailInvalid');
      }
      if (!formData.mobile.trim()) {
        newErrors.mobile = t('contactInfo.errors.mobile');
      } else if (!/^\d{10}$/.test(formData.mobile.replace(/\s/g, ''))) {
        newErrors.mobile = t('contactInfo.errors.mobileInvalid');
      }
      if (!formData.city.trim()) newErrors.city = t('contactInfo.errors.city');
      if (!formData.state.trim()) newErrors.state = t('contactInfo.errors.state');
    }

    if (step === 1) {
      if (!formData.sponsorshipType) newErrors.sponsorshipType = t('sponsorshipInterest.errors.sponsorshipType');
      if (!formData.budgetRange) newErrors.budgetRange = t('sponsorshipInterest.errors.budgetRange');
      if (!formData.modeOfAssociation) newErrors.modeOfAssociation = t('sponsorshipInterest.errors.modeOfAssociation');
    }

    // Step 2 (Organization Details) is optional, no validation needed

    if (step === 3) {
      if (!formData.preferredContactMethod) newErrors.preferredContactMethod = t('communicationPreferences.errors.preferredContactMethod');
      if (!formData.bestTimeToReach) newErrors.bestTimeToReach = t('communicationPreferences.errors.bestTimeToReach');
    }

    // Step 4 (Review) - no validation needed, just review

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    try {
      setLoading(true);
      console.log('Submitting sponsorship:', formData);

      const result = await ApiService.submitSponsorship(formData);

      console.log('Submission successful:', result);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.message || t('messages.submitError'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ backgroundColor: '#FFFFFF' }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            overflow: 'hidden',
            backgroundImage: `url("${t('hero.backgroundImage')}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
            }}
          />

          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <CheckCircle
                size={80}
                style={{ color: theme.palette.primary.main, marginBottom: '24px' }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 'bold',
                  mb: 3,
                  lineHeight: 1.2,
                  background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('success.title')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: 700,
                  mx: 'auto',
                  color: theme.palette.secondary.main,
                }}
              >
                {t('success.message')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 5,
                  opacity: 0.8,
                  color: '#CCCCCC',
                }}
              >
                {t('success.subtitle')}
              </Typography>
              <Button
                variant="contained"
                href="/"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  py: 2,
                  px: 5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 8px 24px rgba(212, 167, 71, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(212, 167, 71, 0.4)'
                  }
                }}
              >
                {t('success.backToHome')}
              </Button>
            </motion.div>
          </Container>
        </Box>
      </Box>
    );
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('contactInfo.fields.fullName')}
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('contactInfo.fields.designation')}
                  name="designation"
                  placeholder={t('contactInfo.fields.designationPlaceholder')}
                  value={formData.designation}
                  onChange={handleChange}
                  error={!!errors.designation}
                  helperText={errors.designation}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  label={t('contactInfo.fields.organization')}
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  error={!!errors.organization}
                  helperText={errors.organization}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('contactInfo.fields.email')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('contactInfo.fields.mobile')}
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  error={!!errors.mobile}
                  helperText={errors.mobile}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('contactInfo.fields.city')}
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('contactInfo.fields.state')}
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  error={!!errors.state}
                  helperText={errors.state}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('sponsorshipInterest.fields.sponsorshipType')}
                  name="sponsorshipType"
                  value={formData.sponsorshipType}
                  onChange={handleChange}
                  error={!!errors.sponsorshipType}
                  helperText={errors.sponsorshipType}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {sponsorshipTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('sponsorshipInterest.fields.budgetRange')}
                  name="budgetRange"
                  value={formData.budgetRange}
                  onChange={handleChange}
                  error={!!errors.budgetRange}
                  helperText={errors.budgetRange}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {budgetRanges.map((range) => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('sponsorshipInterest.fields.modeOfAssociation')}
                  name="modeOfAssociation"
                  value={formData.modeOfAssociation}
                  onChange={handleChange}
                  error={!!errors.modeOfAssociation}
                  helperText={errors.modeOfAssociation}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {modesOfAssociation.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 4,
                textAlign: 'center'
              }}
            >
              {t('organizationDetails.subtitle')}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label={t('organizationDetails.fields.organizationType')}
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleChange}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {organizationTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('organizationDetails.fields.website')}
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder={t('organizationDetails.fields.websitePlaceholder')}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('organizationDetails.fields.organizationNote')}
                  name="organizationNote"
                  value={formData.organizationNote}
                  onChange={handleChange}
                  placeholder={t('organizationDetails.fields.organizationNotePlaceholder')}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('communicationPreferences.fields.preferredContactMethod')}
                  name="preferredContactMethod"
                  value={formData.preferredContactMethod}
                  onChange={handleChange}
                  error={!!errors.preferredContactMethod}
                  helperText={errors.preferredContactMethod}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {contactMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('communicationPreferences.fields.bestTimeToReach')}
                  name="bestTimeToReach"
                  value={formData.bestTimeToReach}
                  onChange={handleChange}
                  error={!!errors.bestTimeToReach}
                  helperText={errors.bestTimeToReach}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {timings.map((time) => (
                    <MenuItem key={time.value} value={time.value}>
                      {time.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.receiveUpdates}
                      onChange={handleChange}
                      name="receiveUpdates"
                      sx={{
                        color: theme.palette.primary.main,
                        '&.Mui-checked': {
                          color: theme.palette.primary.dark,
                        },
                      }}
                    />
                  }
                  label={t('communicationPreferences.fields.receiveUpdates')}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Box
              sx={{
                textAlign: 'center',
                mb: 4,
                pb: 3,
                borderBottom: '2px solid rgba(212, 167, 71, 0.1)'
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#1A1A1A',
                  fontWeight: 700,
                  mb: 1
                }}
              >
                {t('reviewSubmit.title')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontSize: '0.95rem'
                }}
              >
                {t('reviewSubmit.subtitle')}
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              {/* Contact Information */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <User size={18} />
                      {t('reviewSubmit.sections.contactInfo')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(0)}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          borderColor: 'rgba(212, 167, 71, 0.4)',
                        }
                      }}
                    >
                      {t('reviewSubmit.buttons.edit')}
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.fullName')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.fullName}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.designation')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.designation}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.organization')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.organization}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.email')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.email}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.mobile')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.mobile}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.city')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.city}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.state')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.state}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Sponsorship Interest */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <TrendingUp size={18} />
                      {t('reviewSubmit.sections.sponsorshipInterest')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(1)}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          borderColor: 'rgba(212, 167, 71, 0.4)',
                        }
                      }}
                    >
                      {t('reviewSubmit.buttons.edit')}
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.sponsorshipType')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.sponsorshipType}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.budgetRange')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.budgetRange}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.modeOfAssociation')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.modeOfAssociation}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Organization Details */}
              {(formData.organizationType || formData.website || formData.organizationNote) && (
                <Grid size={{ xs: 12 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: '10px',
                      border: '1px solid rgba(212, 167, 71, 0.15)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(212, 167, 71, 0.3)',
                        boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#B8901C',
                          fontSize: '1rem',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Building size={18} />
                        {t('reviewSubmit.sections.organizationDetails')}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setActiveStep(2)}
                        startIcon={<Edit size={14} />}
                        sx={{
                          color: '#D4A747',
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '6px',
                          border: '1px solid rgba(212, 167, 71, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(212, 167, 71, 0.08)',
                            borderColor: 'rgba(212, 167, 71, 0.4)',
                          }
                        }}
                      >
                        {t('reviewSubmit.buttons.edit')}
                      </Button>
                    </Box>
                    <Grid container spacing={1.5}>
                      {formData.organizationType && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('reviewSubmit.fields.organizationType')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.organizationType}</Typography>
                        </Grid>
                      )}
                      {formData.website && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('reviewSubmit.fields.website')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500, wordBreak: 'break-all' }}>{formData.website}</Typography>
                        </Grid>
                      )}
                      {formData.organizationNote && (
                        <Grid size={{ xs: 12 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('reviewSubmit.fields.aboutOrganization')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.7 }}>{formData.organizationNote}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Communication Preferences */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Phone size={18} />
                      {t('reviewSubmit.sections.communicationPreferences')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(3)}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          borderColor: 'rgba(212, 167, 71, 0.4)',
                        }
                      }}
                    >
                      {t('reviewSubmit.buttons.edit')}
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.preferredContactMethod')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.preferredContactMethod}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.bestTimeToReach')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>{formData.bestTimeToReach}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t('reviewSubmit.fields.receiveUpdates')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', color: '#1A1A1A', fontWeight: 500 }}>
                        {formData.receiveUpdates ? t('reviewSubmit.receiveUpdatesText') : 'No'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ backgroundColor: '#FFFFFF' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '50vh',
          mt: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'hidden',
          backgroundImage: `url("${t('hero.backgroundImage')}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
          }}
        />

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.light',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('hero.overline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 2,
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 2,
                opacity: 0.9,
                maxWidth: 800,
                color: theme.palette.secondary.main,
                mx: 'auto',
              }}
            >
              {t('hero.subtitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                opacity: 0.8,
                maxWidth: 900,
                color: theme.palette.secondary.main,
                mx: 'auto',
              }}
            >
              {t('hero.description')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Form Section */}
      <Container maxWidth="lg" sx={{ py: 4}}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: "10px",
              background: '#FFFFFF',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Stepper */}
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<CustomStepConnector />}
              sx={{
                mb: 4,
                '& .MuiStepLabel-root': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 3,
                },
                '& .MuiStepLabel-iconContainer': {
                  paddingRight: 0,
                  paddingLeft: 0,
                  position: 'relative',
                  zIndex: 5,
                  marginBottom: 0,
                },
                '& .MuiStepLabel-labelContainer': {
                  marginTop: '4px !important',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                },
                '& .MuiStepConnector-root': {
                  top: { xs: 20, md: 22 },
                  left: 'calc(-50% + 16px)',
                  right: 'calc(50% + 16px)',
                  zIndex: 1,
                },
                '& .MuiStepConnector-line': {
                  zIndex: 1,
                },
              }}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={({ active, completed }) => (
                      <Box
                        sx={{
                          width: { xs: 40, md: 44 },
                          height: { xs: 40, md: 44 },
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: completed || active ? 'white' : '#D4A747',
                          border: completed || active
                            ? 'none'
                            : '2px solid rgba(212, 167, 71, 0.15)',
                          boxShadow: '0 2px 8px rgba(212, 167, 71, 0.1)',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          zIndex: 10,
                          mt: 0,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: '-1.5px',
                            borderRadius: '50%',
                            background: completed || active ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)' : '#ffffff',
                            zIndex: -1,
                          },
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: completed || active
                              ? '0 8px 20px rgba(212, 167, 71, 0.3)'
                              : '0 4px 12px rgba(212, 167, 71, 0.15)'
                          }
                        }}
                      >
                        {completed ? <CheckCircle size={20} color="white" /> : React.cloneElement(step.icon, { size: 20 })}
                      </Box>
                    )}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={activeStep === index ? 700 : 500}
                      sx={{
                        mt: 1,
                        color: activeStep === index ? '#B8901C' : 'text.secondary',
                        fontSize: { xs: '0.75rem', md: '0.8rem' },
                        lineHeight: 1.3,
                        textAlign: 'center',
                        maxWidth: '90px',
                        mx: 'auto',
                        display: 'block',
                        wordWrap: 'break-word',
                        hyphens: 'auto'
                      }}
                    >
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Error Alert */}
            {submitError && (
              <Alert
                severity="error"
                onClose={() => setSubmitError('')}
                sx={{
                  mb: 3,
                  borderRadius: '8px',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                }}
              >
                {submitError}
              </Alert>
            )}

            {/* Form Content */}
            <Box>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 4,
                  pt: 3,
                  borderTop: '2px solid #F0F0F0',
                }}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  startIcon={<ChevronLeft size={18} />}
                  variant="outlined"
                  sx={{
                    borderRadius: '5px',
                    borderColor: activeStep === 0 ? 'grey.300' : '#D4A747',
                    color: activeStep === 0 ? 'grey.400' : '#D4A747',
                    py: 1,
                    px: 2.5,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: activeStep === 0 ? 'grey.300' : '#B8901C',
                      backgroundColor: activeStep === 0 ? 'transparent' : 'rgba(212, 167, 71, 0.05)',
                      transform: activeStep === 0 ? 'none' : 'translateY(-1px)'
                    }
                  }}
                >
                  {t('navigation.back')}
                </Button>

                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    endIcon={loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <Send size={18} />}
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      color: 'white',
                      borderRadius: '5px',
                      py: 1,
                      px: 3,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 16px rgba(212, 167, 71, 0.25)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px rgba(212, 167, 71, 0.35)'
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.5) 0%, rgba(184, 144, 28, 0.5) 100%)',
                        color: 'rgba(255, 255, 255, 0.7)',
                      }
                    }}
                  >
                    {loading ? t('reviewSubmit.buttons.submitting') : t('reviewSubmit.buttons.submit')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ChevronRight size={18} />}
                    sx={{
                      background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      color: 'white',
                      borderRadius: '5px',
                      py: 1,
                      px: 3,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 16px rgba(212, 167, 71, 0.25)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px rgba(212, 167, 71, 0.35)'
                      }
                    }}
                  >
                    {t('navigation.next')}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Sponsor;
